import { create } from 'zustand';
import type { Client } from '@microsoft/microsoft-graph-client';
import { readJson, writeJson } from '@/lib/graph/onedrive-helpers';
import type { MemoryItem, MemoryDocument, MemoryKind } from '@/types/memory';

// Cache duration: 5 minutes
const CACHE_DURATION_MS = 5 * 60 * 1000;

interface MemoriesState {
  memories: MemoryItem[];
  isLoading: boolean;
  error: string | null;
  lastLoadedAt: number | null;
  loadedUserId: string | null;

  // Actions
  loadMemories: (graph: Client, userId: string, forceRefresh?: boolean) => Promise<void>;
  addMemory: (graph: Client, userId: string, item: Omit<MemoryItem, 'id' | 'updatedUtc'>) => Promise<void>;
  updateMemory: (graph: Client, userId: string, id: string, updates: Partial<MemoryItem>) => Promise<void>;
  deleteMemory: (graph: Client, userId: string, id: string) => Promise<void>;
  getMemoriesByKind: (kind: MemoryKind) => MemoryItem[];
  invalidateCache: () => void;
}

function getMemoriesPath(userId: string): string {
  return `memories/${userId}.json`;
}

export const useMemoriesStore = create<MemoriesState>((set, get) => ({
  memories: [],
  isLoading: false,
  error: null,
  lastLoadedAt: null,
  loadedUserId: null,

  loadMemories: async (graph: Client, userId: string, forceRefresh = false) => {
    const { memories, lastLoadedAt, loadedUserId, isLoading } = get();
    
    // Skip if already loading
    if (isLoading) return;
    
    // Check cache validity (also invalidate if user changed)
    const now = Date.now();
    const isCacheValid = memories.length >= 0 && 
      lastLoadedAt && 
      loadedUserId === userId &&
      (now - lastLoadedAt < CACHE_DURATION_MS);
    
    if (isCacheValid && !forceRefresh) {
      return; // Use cached data
    }

    set({ isLoading: true, error: null });

    try {
      const doc = await readJson<MemoryDocument>(graph, getMemoriesPath(userId));
      set({ 
        memories: doc?.items ?? [], 
        isLoading: false, 
        lastLoadedAt: Date.now(),
        loadedUserId: userId,
      });
    } catch (error) {
      console.error('Failed to load memories:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to load memories',
        isLoading: false,
      });
    }
  },

  addMemory: async (graph: Client, userId: string, item: Omit<MemoryItem, 'id' | 'updatedUtc'>) => {
    const { memories } = get();
    
    const newMemory: MemoryItem = {
      ...item,
      id: crypto.randomUUID(),
      updatedUtc: new Date().toISOString(),
    };

    const updatedMemories = [newMemory, ...memories];
    
    set({ isLoading: true, error: null });

    try {
      await writeJson(graph, getMemoriesPath(userId), { items: updatedMemories });
      set({ memories: updatedMemories, isLoading: false, lastLoadedAt: Date.now() });
    } catch (error) {
      console.error('Failed to add memory:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to add memory',
        isLoading: false,
      });
    }
  },

  updateMemory: async (graph: Client, userId: string, id: string, updates: Partial<MemoryItem>) => {
    const { memories } = get();
    
    const updatedMemories = memories.map(m => 
      m.id === id 
        ? { ...m, ...updates, updatedUtc: new Date().toISOString() }
        : m
    );

    set({ isLoading: true, error: null });

    try {
      await writeJson(graph, getMemoriesPath(userId), { items: updatedMemories });
      set({ memories: updatedMemories, isLoading: false, lastLoadedAt: Date.now() });
    } catch (error) {
      console.error('Failed to update memory:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to update memory',
        isLoading: false,
      });
    }
  },

  deleteMemory: async (graph: Client, userId: string, id: string) => {
    const { memories } = get();
    
    const updatedMemories = memories.filter(m => m.id !== id);

    set({ isLoading: true, error: null });

    try {
      await writeJson(graph, getMemoriesPath(userId), { items: updatedMemories });
      set({ memories: updatedMemories, isLoading: false, lastLoadedAt: Date.now() });
    } catch (error) {
      console.error('Failed to delete memory:', error);
      set({
        error: error instanceof Error ? error.message : 'Failed to delete memory',
        isLoading: false,
      });
    }
  },

  getMemoriesByKind: (kind: MemoryKind) => {
    return get().memories.filter(m => m.kind === kind);
  },

  invalidateCache: () => {
    set({ lastLoadedAt: null });
  },
}));
